import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

export default function Footer() {
  const { t } = useLang();

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/leistungen", label: t("nav.services") },
    { to: "/haeuser", label: "Häuser" },
    { to: "/sanierung", label: "Sanierung & Revitalisierung" },
    { to: "/grundstuecke", label: "Grundstücke & Ankauf" },
    { to: "/investoren", label: "Investoren & Projektpartner" },
    { to: "/projekte", label: t("nav.projects") },
    { to: "/partner/budova", label: t("nav.partner_budova") },
    { to: "/ueber-uns", label: t("nav.about") },
    { to: "/kontakt", label: t("nav.contact") },
  ];

  return (
    <footer className="bg-[#051630] border-t border-white/10 mt-12">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 border border-white flex items-center justify-center font-display text-xl font-bold">
              R
            </span>
            <div className="leading-none">
              <p className="font-display text-base tracking-widest uppercase">
                Royalhouse
              </p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                Bauträger GmbH
              </p>
            </div>
          </div>
          <p className="text-sm text-white/65 max-w-md leading-relaxed">
            Royal House Bauträger GmbH entwickelt, plant und realisiert Wohnprojekte
            in ausgewählten europäischen Lagen. Die technische Gebäudeausrüstung
            verantwortet unser Haustechnik-Partner ECO Building Technik.
          </p>
          <div className="flex flex-wrap items-center gap-6 text-xs text-white/50 tracking-[0.18em] uppercase">
            <span>BIM-zertifiziert</span>
            <span className="w-1 h-1 bg-white/30" />
            <span>Eurocode-konform</span>
            <span className="w-1 h-1 bg-white/30" />
            <span>Wien · Österreich</span>
          </div>
          {/* Partner badge */}
          <div className="pt-2">
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-3">
              {t("common.partner")}
            </p>
            <Link
              to="/partner/budova"
              data-testid="footer-budova-link"
              className="inline-flex items-center gap-3 border border-white/15 px-4 py-2.5 hover:border-white transition-colors group"
            >
              <span className="font-display text-base tracking-tight">Budova</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/50 group-hover:text-white">
                Ekosystem
              </span>
              <ArrowUpRight size={14} strokeWidth={1.5} className="text-white/60 group-hover:text-white" />
            </Link>
          </div>
        </div>

        <div className="md:col-span-3 space-y-4">
          <p className="text-xs tracking-[0.25em] uppercase text-white/50">
            Navigation
          </p>
          <ul className="space-y-2 text-sm">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/70 hover:text-white inline-flex items-center gap-2 group"
                >
                  {l.label}
                  <ArrowUpRight
                    size={14}
                    strokeWidth={1.5}
                    className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 space-y-4">
          <p className="text-xs tracking-[0.25em] uppercase text-white/50">
            {t("nav.contact")}
          </p>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-3">
              <MapPin size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span>
                Bäckerstraße 2/23
                <br />
                A-1010 Wien · Österreich
                <br />
                <span className="text-white/45 text-[11px]">UID: ATU77490127</span>
              </span>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} strokeWidth={1.5} className="shrink-0" />
              <a
                href="mailto:office@royalhouse-building.eu"
                className="hover:text-white"
                data-testid="footer-email-1"
              >
                office@royalhouse-building.eu
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} strokeWidth={1.5} className="shrink-0" />
              <a
                href="mailto:royalhouse.bautraeger@gmail.com"
                className="hover:text-white"
                data-testid="footer-email-2"
              >
                royalhouse.bautraeger@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={16} strokeWidth={1.5} className="shrink-0" />
              <a href="tel:+436643289599" className="hover:text-white">
                +43 664 328 95 99
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40 tracking-wider">
          <p>
            © {new Date().getFullYear()} Royalhouse Bauträger GmbH — {t("common.all_rights")}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">
              {t("common.admin")}
            </Link>
            <Link to="/impressum" className="hover:text-white" data-testid="footer-imprint-link">
              {t("common.imprint")}
            </Link>
            <Link to="/datenschutz" className="hover:text-white" data-testid="footer-privacy-link">
              {t("common.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
